"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTrackPointAtTime = exports.TrackPointIndex = exports.getTrackPointAtIndex = void 0;
var Track_1 = require("../../DataModel/Flight/Track");
/**
 * Gets the point on a track using a floating point index, allowing interpolation between 'real' points within the track.
 * @remarks
 * Indexes will be constrained to be between 0 and the number of points within the line.  The returned point is
 * interpolated linearly, such that an index of 3.5 will return the point half-way between points 3 and 4.
 * @param track - The track from which to extract a point.
 * @param index - The floating point index used to extract the point, interpolating between 'real' points if the number has a decimal component.
 * @returns - Returns a track point on the track at the supplied index.
 */
function getTrackPointAtIndex(track, index) {
    var indexBefore = Math.floor(index);
    var indexAfter = Math.ceil(index);
    if (indexBefore == indexAfter && indexBefore >= 0 && indexBefore <= track.points.length - 1) {
        // We've been given a whole number integer index - so just return the exact point.
        return track.points[indexBefore];
    }
    else if (indexBefore < 0) {
        // Return the first point.
        return track.points[0];
    }
    else if (indexAfter > track.points.length - 1) {
        // Return the last point
        return track.points[track.points.length - 1];
    }
    else {
        // We're half-way between points - find the bounding points and linearly interpolate between them.
        var pointBefore = track.points[indexBefore];
        var pointAfter = track.points[indexAfter];
        var delta = index - indexBefore;
        return lerp(pointBefore, pointAfter, delta);
    }
}
exports.getTrackPointAtIndex = getTrackPointAtIndex;
/**
 * Represents a track point within a track, holding the actual track point and the floating point index within
 * the source track's points array.
 */
var TrackPointIndex = /** @class */ (function () {
    function TrackPointIndex(point, index) {
        this.point = point;
        this.index = index;
    }
    return TrackPointIndex;
}());
exports.TrackPointIndex = TrackPointIndex;
/**
 * Gets the point on a track at a specified time, allowing interpolation between 'real' points within the track.
 * @remarks
 * Track points will be constrained to be between the start and end of the track.  If the time is between two recorded points, the returned
 * value is interpolated linearly, assuming that the aircraft travelled at a constant speed between the points.
 * @param track - The track from which to extract a point.
 * @param time - The time at which to get the track point.
 * @returns - Returns a track point on the track at the supplied time, clamped to the start or end of the track if necessary.
 */
function getTrackPointAtTime(track, time) {
    if (track.points == null || track.points.length == 0) {
        // Just a dummy value.
        return new TrackPointIndex(new Track_1.TrackPoint(0, 0, 0, 0), 0);
    }
    var targetTimeOffset = (time.getTime() - track.startTime.getTime()) / 1000;
    if (targetTimeOffset <= 0) {
        // The target time is before the start time, so return the first point.
        return new TrackPointIndex(track.points[0], 0);
    }
    else if (targetTimeOffset >= track.points[track.points.length - 1].t) {
        // The target time is after the end time, so return the last point.
        return new TrackPointIndex(track.points[track.points.length - 1], track.points.length - 1);
    }
    // Quick search to find it, using a lower index, upper index and a mid index (that's half-way between lower and upper indexes).
    //           *                  The star is the target time.
    // L----------M----------U      The lower, upper and mid index in pass 1.
    // L-----M----U                 The lower, upper and mid index in pass 2.
    //       L--M-U                 The lower, upper and mid index in pass 3.
    //           *                  Found it
    var lowerIndex = 0;
    var upperIndex = track.points.length - 1;
    while (lowerIndex < upperIndex - 1) {
        // Find the mid index between lower and upper index.
        var midIndex = lowerIndex + Math.floor((upperIndex - lowerIndex) / 2);
        if (track.points[midIndex].t == targetTimeOffset) {
            // The point matches the time offset exactly - setting these values will cause the loop to exit.
            lowerIndex = midIndex;
            upperIndex = lowerIndex + 1;
        }
        else if (track.points[midIndex].t < targetTimeOffset) {
            // The mid index point time is lower than the target time.
            // Make lower = mid so the next pass looks in the second-half of the index range
            lowerIndex = midIndex;
        }
        else if (track.points[midIndex].t > targetTimeOffset) {
            // The mid index point time is higher than the target time.
            // Make upper = mid so the next pass looks in the first-half of the index range
            upperIndex = midIndex;
        }
    }
    // lowerIndex and upperIndex will now be 1 apart.
    // Find the gap between these points, and how far from the target time the lowerIndex point is
    var pointTimeDifference = track.points[upperIndex].t - track.points[lowerIndex].t;
    var targetTimeDifference = targetTimeOffset - track.points[lowerIndex].t;
    // Now calculate the ratio between the two.
    // Points may be on top of each other in time, so don't crash if there's a 0 pointTimeDifference.
    var ratio = pointTimeDifference > 0 ? (targetTimeDifference / pointTimeDifference) : 0;
    // The point index is the first index, plus the ratio between the two points for the target time.
    var index = lowerIndex + ratio;
    var point = getTrackPointAtIndex(track, index);
    var retval = new TrackPointIndex(point, index);
    return retval;
}
exports.getTrackPointAtTime = getTrackPointAtTime;
/**
 * Linearly interpolate between two track points, using a ratio that's between (0..1) as the factor.
 * @remarks This method is intentionally not exported, so it remains private to this file.
 * @param start - The first track point
 * @param end - The second track point
 * @param amount - The amount to interpolate, where 0 will return the start point and 1 will return the end point.
*/
function lerp(start, end, amount) {
    amount = amount < 0 ? 0 : amount > 1 ? 1 : amount;
    var lat = start.lat + ((end.lat - start.lat) * amount);
    var lon = start.lon + ((end.lon - start.lon) * amount);
    var alt = start.alt + ((end.alt - start.alt) * amount);
    var t = start.t + ((end.t - start.t) * amount);
    var trackPoint = new Track_1.TrackPoint(lat, lon, alt, t);
    return trackPoint;
}
//# sourceMappingURL=TrackExtensions.js.map